import React from 'react';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import i18n from 'i18next';
import { registerField } from 'core/forms';
import { useModals } from 'core/notifications';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';

import NewItemFormContainer from '@/exceed/containers/newItemFormContainer';

import styles from '../styles/formFieldNewItemForm.module.scss';

const FormFieldNewItemForm = ({ schema = {}, value = null }) => {
  const { addModal } = useModals();

  const addItemModal = async (model, { isDeleting } = {}) => {
    const getModal = () => {
      if (isDeleting) return schema.deleteModal || {};

      return model ? schema.editModal : schema.addModal || {};
    };

    const modal = getModal();
    const id = 'form-field-item';
    const isFullScreen = !isDeleting;
    const deleteString = isDeleting && i18n.t('delete');

    const { payload, type } = await addModal({
      body: modal.body,
      component: (
        <NewItemFormContainer
          getAxiosConfig={modal.getAxiosConfig}
          getSchema={modal.getSchema}
          id={id}
          isFullScreen={isFullScreen}
          isSubmitDisabledIfModelUnchanged={!isDeleting}
          schema={modal.schema}
          submitColor={isDeleting ? 'negative' : 'brand'}
          submitText={modal.submitText || deleteString}
        />
      ),
      hasCloseButton: true,
      id,
      isFullScreen,
      model: model || modal.model,
      title: modal.title || deleteString,
    });

    if (type === 'submit') {
      schema.onSubmit?.(payload);
    }
  };

  const handleAddButtonClick = () => addItemModal();
  const handleDeleteButtonClick = (model) => addItemModal(model, { isDeleting: true });
  const handleEditButtonClick = addItemModal;
  const { isDisabled } = schema;

  return (
    <div className={styles.element}>
      <Button
        icon={<AddIcon />}
        isDisabled={isDisabled}
        onClick={handleAddButtonClick}
        text={schema.addButtonText || i18n.t('add')}
        variant="outlined"
      />
      {!value?.length ? (
        <Typo variant="s01" weight="medium">
          {schema.emptyText}
        </Typo>
      ) : (
        <ul className={styles.items}>
          {value.map((item) => (
            <li className={styles.item} key={item.id}>
              <Typo className={styles.label} variant="s02" weight="medium">
                {schema.getItemLabel?.(item) || item.name}
              </Typo>
              <Button
                ariaLabel={i18n.t('edit')}
                icon={<EditIcon />}
                isDisabled={isDisabled}
                onClick={() => handleEditButtonClick(item)}
                size="xsmall"
                variant="ghost"
              />
              <Button
                ariaLabel={i18n.t('delete')}
                icon={<CloseIcon />}
                isDisabled={isDisabled}
                onClick={() => handleDeleteButtonClick(item)}
                size="xsmall"
                variant="ghost"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const modalPropTypes = PropTypes.shape({
  body: PropTypes.string,
  getAxiosConfig: PropTypes.func,
  getSchema: PropTypes.func,
  model: PropTypes.shape({}),
  schema: PropTypes.shape({}),
  submitText: PropTypes.string,
  title: PropTypes.string,
});

FormFieldNewItemForm.propTypes = {
  schema: PropTypes.shape({
    addButtonText: PropTypes.string,
    addModal: modalPropTypes,
    deleteModal: modalPropTypes,
    editModal: modalPropTypes,
    emptyText: PropTypes.string,
    getItemLabel: PropTypes.func,
    isDisabled: PropTypes.bool,
    onSubmit: PropTypes.func,
  }),
  value: PropTypes.arrayOf(PropTypes.shape({})),
};

registerField('NewItemForm', FormFieldNewItemForm);

export default FormFieldNewItemForm;
